import { html, raw } from 'onefold';
import { Playground } from '../../components/Playground';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function IntroPage(): Node {
  return html`
    <div>
      <div style="text-align:center;margin-bottom:32px">
        <img src="./images/logo.svg" alt="onefold" width="96" height="96" style="display:inline-block;margin-bottom:12px" />
        <h1 style="margin-bottom:4px">onefold</h1>
        <p style="font-size:18px;color:var(--muted);max-width:600px;margin:0 auto">A modern lightweight UI reactive framework for building everything from simple websites to enterprise-scale web applications. Signals, routing, forms, i18n, microfrontend security — no virtual DOM, no compiler, no dependencies.</p>
      </div>

      <div class="hero-stats">
        <div class="hero-stat"><span class="val">~6kb</span><span class="lbl">Core (gzipped)</span></div>
        <div class="hero-stat"><span class="val">0</span><span class="lbl">Dependencies</span></div>
        <div class="hero-stat"><span class="val">TypeScript</span><span class="lbl">First-class</span></div>
        <div class="hero-stat"><span class="val">ES2022</span><span class="lbl">Target</span></div>
      </div>

      <h2>Get Started in Seconds</h2>
      ${CodeBlock(`npm create onefold@latest my-app
cd my-app
npm install
npm run dev`)}

      <h2>Why onefold?</h2>

      <h3>Fine-grained Reactivity</h3>
      <p>Each signal update touches only the exact DOM node that depends on it. No virtual DOM diffing. No tree reconciliation. Updates are O(1) per change.</p>

      <h3>Secure by Default</h3>
      <p>Text interpolation always goes through <code>textContent</code>, never <code>innerHTML</code>. XSS from dynamic data is structurally impossible. Event handler strings are blocked. URL schemes are validated. Trusted Types integration for CSP compliance.</p>

      <h3>Complete Toolkit</h3>
      <p>Everything ships in one package — no decision fatigue, no version mismatches between 10 npm packages:</p>
      <ul>
        <li>Routing (nested, dynamic params, programmatic navigation)</li>
        <li>State management (store, persisted signals, DI)</li>
        <li>Forms with validation (8 built-in rules)</li>
        <li>HTTP client with interceptor pipeline</li>
        <li>Internationalization (reactive locale switching)</li>
        <li>Theming (CSS custom properties)</li>
        <li>Microfrontend security (SRI, Shadow DOM, iframe sandbox)</li>
        <li>Streaming (WebSocket, SSE — reactive)</li>
        <li>SSR (renderHTML — zero dependencies, no jsdom)</li>
      </ul>

      <h3>No Compiler Required</h3>
      <p>No JSX transform, no Babel plugin, no Vite config. The <code>html</code> tagged template works at runtime with any bundler — or no bundler at all. Drop a ${raw('<code>&lt;script type="module"&gt;</code>')} and go.</p>

      <h3>TypeScript-First</h3>
      <p>Built under <code>strict: true</code> with <code>noUncheckedIndexedAccess</code>. Full type inference. Illegal states fail at compile time, not at runtime.</p>

      ${Callout('onefold is what you get when you take a fine-grained signal engine, remove the compiler requirement, and ship the entire application toolkit in one package with enterprise security built into the foundation.')}

      <h2>Quick Example</h2>

      <h3>Static rendering</h3>
      <p>The <code>html</code> tagged template creates real DOM nodes. No compilation step — this is runtime code:</p>

      ${Playground(`import { html, mount } from 'onefold';

// Static — no signals, just HTML
const welcome = html\`
  <div style="text-align:center">
    <h1>Welcome to onefold!</h1>
    <p>Edit this code and click Run.</p>
    <p>No build step. No compiler. Just tagged templates.</p>
  </div>
\`;

mount(welcome, document.getElementById('app'));`, 'Static Rendering')}

      <h3>Dynamic rendering (reactive)</h3>
      <p>Wrap values in ${raw('<code>() =&gt;</code>')} to make them reactive. The framework tracks which DOM node reads which signal and updates only that node when the signal changes:</p>

      ${Playground(`import { createSignal, createComputed, html, mount } from 'onefold';

function Counter() {
  const count = createSignal(0);
  const double = createComputed(() => count() * 2);

  return html\`
    <div style="text-align:center">
      <h2>Count: \${() => count()}</h2>
      <p>Double: \${() => double()}</p>
      <div style="display:flex;gap:8px;justify-content:center">
        <button onclick=\${() => count.set(n => n - 1)}>-</button>
        <button onclick=\${() => count.set(n => n + 1)}>+</button>
        <button onclick=\${() => count.set(0)}>Reset</button>
      </div>
    </div>
  \`;
}

mount(Counter(), document.getElementById('app'));`, 'Signals + Computed')}

      <h2>Architecture</h2>
      <p>onefold uses fine-grained signals bound directly to real DOM nodes:</p>
      ${CodeBlock(`Signal changes → Effect runs → One DOM node updates

// No virtual DOM tree
// No diffing algorithm
// No reconciliation pass
// No scheduler queue
// Just: signal.set(newValue) → that one <span> updates`)}

      <p>This is measurably faster than virtual DOM reconciliation on update-heavy workloads because it skips the diff step entirely.</p>

      <h2>What's Included</h2>
      <table>
        <tr><th>Category</th><th>Features</th></tr>
        <tr><td><strong>Core</strong></td><td>createSignal, createEffect, createComputed, batch, html, css, mount</td></tr>
        <tr><td><strong>Routing</strong></td><td>Router, nested routes, dynamic params, navigate, Link</td></tr>
        <tr><td><strong>State</strong></td><td>createStore, createPersisted, provide/inject (DI)</td></tr>
        <tr><td><strong>Data</strong></td><td>createResource, createHttpClient, interceptors</td></tr>
        <tr><td><strong>Forms</strong></td><td>createForm, required, email, minLength, maxLength, pattern, min, max, custom</td></tr>
        <tr><td><strong>Microfrontends</strong></td><td>loadRemote, configureSecurity, SRI integrity, Shadow DOM/iframe isolation</td></tr>
        <tr><td><strong>i18n</strong></td><td>createI18n, reactive locale switching, interpolation</td></tr>
        <tr><td><strong>Theming</strong></td><td>createTheme, CSS custom properties, toggle</td></tr>
        <tr><td><strong>Async</strong></td><td>Suspense, SuspenseAll, ErrorBoundary, lazy</td></tr>
        <tr><td><strong>Streaming</strong></td><td>createWebSocket, createEventSource (reactive signals)</td></tr>
        <tr><td><strong>Accessibility</strong></td><td>FocusTrap, announce, useKeyboard, SkipLink</td></tr>
        <tr><td><strong>Performance</strong></td><td>VirtualList (windowed rendering), code splitting</td></tr>
        <tr><td><strong>Interop</strong></td><td>wrapImperative (Chart.js, D3), embedForeign (React, Vue)</td></tr>
        <tr><td><strong>Security</strong></td><td>RBAC guards, XSS prevention, Trusted Types, cssValue sanitizer</td></tr>
        <tr><td><strong>SSR</strong></td><td>renderHTML (zero deps, no jsdom, ~0.5ms/page)</td></tr>
        <tr><td><strong>DevTools</strong></td><td>enableDevtools, render profiling, auto-labeling, signal tracking</td></tr>
        <tr><td><strong>Utilities</strong></td><td>formatDate, timeAgo, formatCurrency, debounce, throttle, pipe, slugify, pluralize</td></tr>
      </table>

      <h2>Comparison</h2>
      <table>
        <tr><th>Framework</th><th>Core Size (gzip)</th><th>Core Includes</th><th>Full Ecosystem</th><th>Compiler</th></tr>
        <tr><td><strong>onefold</strong></td><td><strong>~6kb</strong></td><td>Signals, html, Router, Store, CSS, DI, lazy</td><td>~16kb (forms, http, i18n, MFE, SSR, a11y...)</td><td>None</td></tr>
        <tr><td>React 19 + ReactDOM</td><td>~42kb</td><td>VDOM + reconciler</td><td>70-120kb+ (router, state, forms via npm)</td><td>JSX transform</td></tr>
        <tr><td>Vue 3</td><td>~16-33kb</td><td>Reactivity + templates</td><td>50-80kb+ (vue-router, Pinia via npm)</td><td>SFC compiler</td></tr>
        <tr><td>SolidJS</td><td>~7kb</td><td>Signals + JSX</td><td>30-50kb+ (@solidjs/router, store via npm)</td><td>JSX + Babel plugin</td></tr>
        <tr><td>Svelte 5</td><td>~3-5kb runtime</td><td>Runes + compiled output</td><td>30-50kb+ (SvelteKit for routing, forms)</td><td>Svelte compiler (required)</td></tr>
      </table>
      <p style=${{ marginTop: '12px', fontSize: '13px', color: 'var(--muted)' }}>
        onefold's ~6kb core covers what most apps need. Enterprise features (forms, HTTP, i18n, microfrontends, SSR) 
        are available via sub-path imports and tree-shake independently — you only pay for what you use.
        Other frameworks require installing separate npm packages for equivalent functionality.
      </p>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/getting-started/install">Installation</a> — npm, CDN, or scaffold a project</li>
        <li><a href="/getting-started/quickstart">Quick Start</a> — build your first app in 2 minutes</li>
        <li><a href="/core/signals">Signals</a> — the reactivity primitive</li>
        <li><a href="/core/templates">Templates</a> — how to write UI with <code>html</code></li>
        <li><a href="/playground">Playground</a> — experiment with code live in the browser</li>
      </ul>
    </div>
  `;
}
